import {
  Box,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

const EventCell = ({ icon, title }: { icon?: string; title?: string }) => {
  if (!title && !icon) return <Typography variant="caption" color="text.secondary">-</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {icon && (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ width: 24, height: 24, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0 }}
        />
      )}
      {title && (
        <Tooltip title={title}>
          <Typography
            variant="caption"
            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
          >
            {title}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
};
import { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import dayjs from 'dayjs';
import {
  PredictionPosition,
  PredictionTradeItem,
  fetchPredictionPositions,
  fetchPredictionTradeHistory,
} from '../../services/predictionService';

const fmtNum = (v: string | number, decimals = 6) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const PnlText = ({ value }: { value: string }) => {
  const n = Number(value);
  const color = n > 0 ? 'success.main' : n < 0 ? 'error.main' : 'text.primary';
  const prefix = n > 0 ? '+' : '';
  return (
    <Typography variant="body2" color={color} fontWeight={600}>
      {prefix}{fmtNum(value)}
    </Typography>
  );
};

const kindColor = (kind: string): 'success' | 'error' | 'warning' | 'default' => {
  if (kind === 'BUY' || kind === 'CLAIM') return 'success';
  if (kind === 'SELL' || kind === 'LOSS') return 'error';
  return 'default';
};

const truncateId = (id: string, len = 12) =>
  id.length > len * 2 ? `${id.slice(0, len)}…${id.slice(-len)}` : id;

// ─── Positions ────────────────────────────────────────────────────────────────

export const PredictPositions = ({ record }: { record: any }) => {
  const t = useTranslate();
  const [rows, setRows] = useState<PredictionPosition[]>([]);
  const [loading, setLoading] = useState(false);

  const userId = record?.data?.id;

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setLoading(true);
    fetchPredictionPositions(userId)
      .then((data) => { if (active) setRows(data ?? []); })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  if (loading) return <Skeleton height={320} width="100%" />;
  if (rows.length === 0) return <EmptyContent title={t('common.noData', { _: 'No data' })} />;

  return (
    <TableContainer>
      <Table size="small" sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('module.userInfo.predict.event', { _: 'Event' })}</TableCell>
            <TableCell align="right">{t('module.userInfo.predict.openQty', { _: 'Open Qty' })}</TableCell>
            <TableCell align="right">{t('module.userInfo.predict.costBasis', { _: 'Cost Basis' })}</TableCell>
            <TableCell align="right">{t('module.userInfo.predict.realizedPnl', { _: 'Realized PnL' })}</TableCell>
            <TableCell align="right">{t('module.userInfo.predict.totalFees', { _: 'Total Fees' })}</TableCell>
            <TableCell>{t('module.userInfo.predict.lastEvent', { _: 'Last Event' })}</TableCell>
            <TableCell>{t('module.userInfo.predict.closedAt', { _: 'Closed At' })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.assetId} hover>
              <TableCell><EventCell icon={row.event?.icon} title={row.event?.title} /></TableCell>
              <TableCell align="right">{fmtNum(row.openQty)}</TableCell>
              <TableCell align="right">{fmtNum(row.costBasis)}</TableCell>
              <TableCell align="right"><PnlText value={row.realizedPnl} /></TableCell>
              <TableCell align="right">{fmtNum(row.totalFees)}</TableCell>
              <TableCell>
                {row.lastEventAt ? dayjs(row.lastEventAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </TableCell>
              <TableCell>
                {row.closedAt ? dayjs(row.closedAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─── Trade History ─────────────────────────────────────────────────────────────

const LIMIT = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100];

export const PredictTradeHistory = ({ record }: { record: any }) => {
  const t = useTranslate();
  const [rows, setRows] = useState<PredictionTradeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LIMIT);
  // cursor stack stored in ref: no re-render on write, avoids infinite effect loop
  const cursorStackRef = useRef<(string | undefined)[]>([undefined]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const userId = record?.data?.id;

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setLoading(true);
    const cursor = cursorStackRef.current[page];
    fetchPredictionTradeHistory(userId, rowsPerPage, cursor)
      .then((data) => {
        if (!active) return;
        setRows(data.items ?? []);
        const next = data.pageInfo?.nextCursor ?? null;
        setHasNextPage(data.pageInfo?.hasNextPage ?? false);
        if (next) {
          cursorStackRef.current[page + 1] = next;
        }
      })
      .catch(() => { if (active) setRows([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId, page, rowsPerPage]);

  const handlePageChange = (_e: unknown, newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(0);
    cursorStackRef.current = [undefined];
  };

  if (loading) return <Skeleton height={320} width="100%" />;
  if (rows.length === 0) return <EmptyContent title={t('common.noData', { _: 'No data' })} />;

  return (
    <>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('module.userInfo.predict.kind', { _: 'Kind' })}</TableCell>
              <TableCell>{t('module.userInfo.predict.event', { _: 'Event' })}</TableCell>
              <TableCell align="right">{t('module.userInfo.predict.qty', { _: 'Qty' })}</TableCell>
              <TableCell align="right">{t('module.userInfo.predict.usdc', { _: 'USDC' })}</TableCell>
              <TableCell align="right">{t('module.userInfo.predict.fee', { _: 'Fee' })}</TableCell>
              <TableCell align="right">{t('module.userInfo.predict.pnlDelta', { _: 'PnL Delta' })}</TableCell>
              <TableCell>{t('module.userInfo.predict.source', { _: 'Source' })}</TableCell>
              <TableCell>{t('module.userInfo.predict.occurredAt', { _: 'Occurred At' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.tradeId} hover>
                <TableCell>
                  <Chip label={row.kind} color={kindColor(row.kind)} size="small" />
                </TableCell>
                <TableCell><EventCell icon={row.event?.icon} title={row.event?.title} /></TableCell>
                <TableCell align="right">{fmtNum(row.qty)}</TableCell>
                <TableCell align="right">${fmtNum(row.usdc)}</TableCell>
                <TableCell align="right">${fmtNum(row.feeUsdc)}</TableCell>
                <TableCell align="right"><PnlText value={row.realizedPnlDelta} /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {row.source}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.occurredAt ? dayjs(row.occurredAt).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={hasNextPage ? -1 : page * rowsPerPage + rows.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  );
};
